<template>
  <section id="historia" class="history-section">
    <vue-headful :title="title" />
    
    <div class="background-elements">
      <div class="timeline-grid"></div>
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="binary-overlay">1988 1990 1995 2000 2005 2010 2013 2020 2024</div>
    </div>

    <div class="content-layer">
      <v-container>
        
        <v-row justify="center" class="hero-history">
          <v-col cols="12" md="10" lg="8" class="text-center overflow-hidden">
            <div class="binary-text-container">
              <div class="binary-text">01001000 01001001 01010011 01010100 01001111 01010010 01001001 01000001 01001000 01001001 01010011 01010100 01001111 01010010 01001001 01000001</div>
            </div>
            
            <div class="title-container">
              <div class="logo-text mb-2">1988 - PRESENTE</div>
              <h1 class="text-h2 text-md-h1 font-weight-black main-title">
                <span class="title-line line-1">HISTORIA DEL</span>
                <span class="title-line line-2 nic-gradient">.NI</span>
              </h1>
              <div class="scan-line"></div>
            </div>
            
            <div class="subtitle-container">
              <p class="text-subtitle-1 text-md-h6 neon-text">
                Más de tres décadas conectando a Nicaragua con el mundo — Más de tres décadas conectando a Nicaragua con el mundo
              </p>
            </div>
          </v-col>
        </v-row>

        <div class="timeline-section-wrapper">
          <div class="timeline-3d-container">
            <div class="timeline-axis"></div>
            
            <div class="timeline-items-container">
              <div 
                v-for="(milestone, index) in milestones" 
                :key="index"
                class="milestone-item-wrapper"
                :class="{'item-top': index % 2 === 0, 'item-bottom': index % 2 !== 0}"
                :style="{ left: `${(index + 1) * (100 / (milestones.length + 1))}%` }"
                @mouseenter="activeMilestone = index"
                @mouseleave="activeMilestone = null"
              >
                <div class="milestone-card" :class="{ 'active': activeMilestone === index }">
                  <div class="milestone-year">{{ milestone.year }}</div>
                  <div class="icon-circle">
                    <v-icon large color="#00BCD4">{{ milestone.icon }}</v-icon>
                  </div>
                  <div class="milestone-title">{{ milestone.title }}</div>
                </div>
                
                <div class="connector-line"></div>
                
                <transition name="fade">
                  <div v-if="activeMilestone === index" class="milestone-detail-popover">
                    <h4>{{ milestone.title }}</h4>
                    <p>{{ milestone.description }}</p>
                    <div class="tags-row">
                      <span v-for="tag in milestone.tags" :key="tag" class="mini-tag">{{tag}}</span>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <div class="org-structure-section">
          <h2 class="section-title text-center mb-10">RECONOCIMIENTOS INTERNACIONALES</h2>
          
          <div class="org-responsive-container" :class="{'is-mobile': isMobile}">
            <div class="org-center-node">
              <div class="center-content">
                <v-icon x-large color="white">mdi-crown</v-icon>
                <h3 class="mt-2">NIC.NI</h3>
                <span class="org-badge">PIONERO</span>
              </div>
            </div>

            <div class="org-satellites-wrapper">
              <div 
                v-for="(org, index) in organizations" 
                :key="index"
                class="org-satellite-item"
                :style="getResponsiveOrgStyle(index)"
                @mouseenter="activeOrg = index"
                @mouseleave="activeOrg = null"
              >
                <div class="satellite-card" :class="{ 'active': activeOrg === index }">
                  <v-icon large color="#00BCD4">{{ org.icon }}</v-icon>
                  <div class="satellite-info">
                    <div class="org-name">{{ org.name }}</div>
                    <div class="org-year">{{ org.year }}</div>
                  </div>
                </div>

                <transition name="scale">
                  <div v-if="activeOrg === index" class="satellite-detail">
                    <strong style="color: #00BCD4">{{ org.fullName }}</strong>
                    <p class="mt-2">{{ org.description }}</p>
                    <div class="role-badge">{{ org.role }}</div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <div class="achievements-section">
          <div class="achievements-header text-center mb-10">
            <h2 class="section-title">LOGROS Y HITOS</h2>
            <p class="section-subtitle neon-text-static">Momentos clave en nuestra trayectoria</p>
          </div>
          
          <div class="achievements-grid">
            <div 
              v-for="(achievement, index) in achievements" 
              :key="index"
              class="achievement-box"
            >
              <div class="achievement-year-badge">{{ achievement.year }}</div>
              <div class="achievement-content-card">
                <div class="icon-header">
                  <div class="icon-bg">
                    <v-icon color="#00BCD4">{{ achievement.icon }}</v-icon>
                  </div>
                  <span class="impact-tag">{{ achievement.impact }}</span>
                </div>
                <h4 class="mt-4 mb-2 white--text">{{ achievement.title }}</h4>
                <p class="grey--text text--lighten-1">{{ achievement.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="history-cta">
          <div class="cta-container">
            <div class="cta-content">
              <h3 class="cta-title">¿QUIERES SER PARTE DE NUESTRA HISTORIA?</h3>
              <p class="cta-text">
                Registra tu dominio .ni y únete a la identidad digital de Nicaragua
              </p>
            </div>
            <div class="cta-actions">
              
            </div>
          </div>
        </div>

      </v-container>
    </div>
  </section>
</template>

<script>
import vueHeadful from "vue-headful";

export default {
  name: "HistoriaPage",
  components: {
    vueHeadful,
  },
  data: () => ({
    title: "Historia | NIC .NI",
    activeMilestone: null,
    activeOrg: null,
    windowWidth: 0,
    
    // DATOS DE HITOS
    milestones: [
      {
        year: 1988,
        icon: "mdi-flag",
        title: "Fundación",
        description: "La UNI asume la administración del dominio .NI según procedimientos internacionales",
        tags: ["UNI", "IANA", "ICANN"]
      },
      {
        year: 1995,
        icon: "mdi-web",
        title: "Segundo en Latam",
        description: "Nos convertimos en el segundo código de dominio abierto en Latinoamérica después de Brasil",
        tags: ["Latam", "Pionero", "RFC1591"]
      },
      {
        year: 2000,
        icon: "mdi-server-network",
        title: "Expansión",
        description: "Implementación de nuevos servicios y expansión de la infraestructura tecnológica",
        tags: ["IPv4", "DNS", "Servidores"]
      },
      {
        year: 2013,
        icon: "mdi-office-building",
        title: "Creación NIC.NI",
        description: "Establecimiento formal de la Dirección NIC.NI mediante Acuerdo de Rectoría No. 810",
        tags: ["Oficial", "Estructura"]
      },
      {
        year: 2020,
        icon: "mdi-chart-timeline",
        title: "Modernización",
        description: "Implementación de sistemas modernos y adopción de estándares internacionales (Tier IV)",
        tags: ["Cloud", "Seguridad"]
      }
    ],

    // DATOS DE ORGANIZACIONES
    organizations: [
      {
        icon: "mdi-earth",
        name: "IANA",
        fullName: "Internet Assigned Numbers Authority",
        role: "Reconocimiento",
        year: "1988",
        description: "Autoridad máxima de asignación de números en Internet"
      },
      {
        icon: "mdi-domain",
        name: "ICANN",
        fullName: "Internet Corp for Assigned Names and Numbers",
        role: "Registro",
        year: "1998",
        description: "Corporación para la asignación de nombres y números en Internet"
      },
      {
        icon: "mdi-server",
        name: "InterNIC",
        fullName: "Internet Network Information Center",
        role: "Servicio",
        year: "1993",
        description: "Centro de información de red de Internet"
      },
      {
        icon: "mdi-map",
        name: "LACTLD",
        fullName: "Latin American and Caribbean TLD",
        role: "Regional",
        year: "1998",
        description: "Organización de dominios de nivel superior de Latinoamérica y Caribe"
      },
      {
        icon: "mdi-ip",
        name: "LACNIC",
        fullName: "Latin American and Caribbean IP Registry",
        role: "Direcciones IP",
        year: "2001",
        description: "Registro de direcciones de Internet para Latinoamérica y Caribe"
      }
    ],

    // DATOS DE LOGROS
    achievements: [
      {
        year: 1989,
        icon: "mdi-ip-network",
        title: "Asignación IPs Bloque B",
        description: "Administración de segmentos de números IPs bloque B: 165.98.0.0 hasta 165.98.254.0",
        impact: "Infraestructura"
      },
      {
        year: 1995,
        icon: "mdi-trophy",
        title: "Segundo en Latam",
        description: ".NI se convierte en el segundo código de dominio abierto en Latinoamérica",
        impact: "Hito Regional"
      },
      {
        year: 2001,
        icon: "mdi-account-group",
        title: "Miembro LACNIC",
        description: "Ingreso como miembro del Latin American and Caribbean Internet Addresses Registry",
        impact: "Reconocimiento"
      },
      {
        year: 2013,
        icon: "mdi-office-building",
        title: "Dirección Formal",
        description: "Creación de la Dirección NIC.NI mediante Acuerdo de Rectoría No. 810",
        impact: "Estructura"
      },
      {
        year: 2020,
        icon: "mdi-shield-check",
        title: "Certificación Seguridad",
        description: "Implementación de sistemas de seguridad Tier IV y certificaciones internacionales",
        impact: "Seguridad"
      }
    ]
  }),
  computed: {
    isMobile() {
      return this.windowWidth < 960;
    }
  },
  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  methods: {
    getResponsiveOrgStyle(index) {
      // En móvil no aplicamos posición absoluta, dejamos que CSS flexbox maneje el orden
      if (this.isMobile) return {}; 
      
      // En escritorio aplicamos la distribución radial
      const total = this.organizations.length;
      // Ajustamos el ángulo para empezar desde arriba (-90 grados o -PI/2)
      const angle = (index / total) * 2 * Math.PI - (Math.PI / 2); 
      const radius = 240; // Radio del círculo de satélites
      
      return {
        position: 'absolute',
        left: '50%',
        top: '50%',
        // Usamos translate para centrar el elemento en su punto calculado
        transform: `translate(calc(-50% + ${Math.cos(angle) * radius}px), calc(-50% + ${Math.sin(angle) * radius}px))`
      };
    }
  }
};
</script>

<style scoped>
/* =========================================
   BASE STYLES & FONDO
   ========================================= */
.history-section {
  background: linear-gradient(135deg, #0a0f2b 0%, #1a1a2e 100%);
  color: white;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  font-family: 'Roboto', sans-serif;
}

.background-elements {
  position: fixed; width: 100%; height: 100%; top: 0; left: 0; pointer-events: none; z-index: 0;
}

.timeline-grid {
  width: 100%; height: 100%;
  background-image: linear-gradient(rgba(0,188,212,0.05) 1px, transparent 1px),
  linear-gradient(90deg, rgba(0,188,212,0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}

.gradient-orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.1; }
.orb-1 { width: 300px; height: 300px; background: #00BCD4; top: 10%; right: -50px; }
.orb-2 { width: 300px; height: 300px; background: #3D5AFE; bottom: 10%; left: -50px; }

.binary-overlay {
  position: absolute; top: 40%; width: 100%; text-align: center;
  font-family: 'Courier New', monospace; color: rgba(255,255,255,0.03);
  font-size: 2rem; white-space: nowrap;
  animation: scroll-left 60s linear infinite;
}

.content-layer { position: relative; z-index: 1; }

/* =========================================
   HERO HEADER & ANIMACIONES DE TEXTO
   ========================================= */
.hero-history { padding: 60px 0 40px; }

/* Contenedores para el efecto de scroll */
.binary-text-container, .subtitle-container {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

/* Animación de scroll horizontal */
@keyframes scroll-left {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

.binary-text {
  font-family: 'Courier New', monospace; color: #00BCD4; opacity: 0.3;
  letter-spacing: 2px; font-size: 0.8rem; margin-bottom: 20px;
  display: inline-block;
  animation: scroll-left 30s linear infinite;
}

.neon-text {
  color: #00BCD4; font-family: 'Courier New', monospace;
  display: inline-block;
  animation: scroll-left 40s linear infinite;
}

/* Clase estática para el subtítulo de la sección de logros */
.neon-text-static {
  color: #00BCD4; font-family: 'Courier New', monospace;
}


.main-title { line-height: 1; }
.title-line { display: block; }
.line-1 { font-size: 2.5rem; color: white; }
.line-2 { font-size: 5rem; line-height: 1; }
.nic-gradient {
  background: linear-gradient(45deg, #00BCD4, #3D5AFE);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
}

.scan-line {
  height: 2px; width: 200px; background: linear-gradient(90deg, transparent, #00BCD4, transparent);
  margin: 20px auto;
}

/* =========================================
   1. TIMELINE ZIGZAG (CORREGIDO)
   ========================================= */
.timeline-section-wrapper {
  overflow-x: auto; /* Scroll horizontal si es necesario */
  padding: 50px 0 100px 0;
  margin-bottom: 50px;
}

.timeline-3d-container {
  position: relative;
  min-width: 800px; /* Asegura que no se aplasten en móvil */
  height: 400px;
  margin: 0 auto;
}

.timeline-axis {
  position: absolute; top: 50%; left: 20px; right: 20px;
  height: 4px; background: rgba(255,255,255,0.1);
  border-radius: 2px; z-index: 0;
}

.milestone-item-wrapper {
  position: absolute;
  transform: translateX(-50%);
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Lógica Zig-Zag */
.item-top { top: 5%; bottom: auto; }
.item-bottom { top: auto; bottom: 5%; flex-direction: column-reverse; }

.connector-line {
  height: 80px; width: 2px;
  background: linear-gradient(to bottom, #00BCD4, transparent);
  opacity: 0.5;
}
.item-bottom .connector-line { background: linear-gradient(to top, #00BCD4, transparent); }

.milestone-card {
  width: 100%;
  background: rgba(10, 15, 43, 0.9);
  border: 1px solid rgba(0,188,212,0.3);
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  z-index: 10;
}

.milestone-card:hover, .milestone-card.active {
  border-color: #00BCD4;
  transform: scale(1.05);
  box-shadow: 0 0 20px rgba(0,188,212,0.4);
  z-index: 20;
}

.milestone-year { font-weight: bold; color: #00BCD4; font-size: 1.2rem; margin-bottom: 5px; }
.icon-circle { margin-bottom: 5px; }
.milestone-title { font-size: 0.9rem; font-weight: 600; line-height: 1.2; }

/* Detalle Popover */
.milestone-detail-popover {
  position: absolute; width: 220px;
  background: rgba(16, 23, 60, 0.98);
  border: 1px solid #00BCD4;
  padding: 15px; border-radius: 8px;
  z-index: 100;
  box-shadow: 0 10px 30px rgba(0,0,0,0.8);
  pointer-events: none;
}
.item-top .milestone-detail-popover { top: 110%; }
.item-bottom .milestone-detail-popover { bottom: 110%; }

.tags-row { display: flex; gap: 5px; margin-top: 10px; flex-wrap: wrap; }
.mini-tag { font-size: 0.7rem; background: rgba(0,188,212,0.2); color: #00BCD4; padding: 2px 6px; border-radius: 4px; }


/* =========================================
   2. ESTRUCTURA ORG (CORREGIDO)
   ========================================= */
.org-structure-section { margin-bottom: 100px; }

.org-responsive-container {
  position: relative;
  min-height: 600px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Mobile Stack Mode */
.org-responsive-container.is-mobile {
  min-height: auto;
  flex-direction: column;
  gap: 40px;
}

.org-center-node {
  width: 160px; height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, #3D5AFE 0%, #0a0f2b 100%);
  border: 2px solid #00BCD4;
  display: flex; align-items: center; justify-content: center;
  z-index: 5;
  box-shadow: 0 0 50px rgba(61, 90, 254, 0.4);
}
.center-content { text-align: center; }
.org-badge {
  background: rgba(255,255,255,0.2); padding: 4px 12px;
  border-radius: 12px; font-size: 0.7rem; font-weight: bold;
}

/* Satellite Styles */
.org-satellite-item { transition: all 0.5s ease; }

.org-responsive-container.is-mobile .org-satellite-item {
  position: relative !important; transform: none !important;
  left: auto !important; top: auto !important;
  width: 100%; max-width: 320px;
}

.satellite-card {
  display: flex; align-items: center; gap: 15px;
  background: rgba(255,255,255,0.05);
  padding: 12px 20px; border-radius: 30px;
  border: 1px solid rgba(255,255,255,0.1);
  cursor: pointer; transition: 0.3s;
  backdrop-filter: blur(5px);
}

.satellite-card:hover, .satellite-card.active {
  background: rgba(0,188,212,0.15); border-color: #00BCD4;
  transform: scale(1.05);
}

.org-name { font-weight: bold; font-size: 1rem; }
.org-year { font-size: 0.8rem; color: #00BCD4; font-family: 'Courier New'; }

.satellite-detail {
  position: absolute;
  top: 130%; left: 50%; transform: translateX(-50%);
  background: #1a1a2e; border: 1px solid #00BCD4;
  padding: 15px; width: 240px; border-radius: 12px;
  z-index: 50; text-align: center; font-size: 0.9rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}
.role-badge { 
  margin-top: 5px; color: #3D5AFE; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px;
}

/* =========================================
   3. LOGROS GRID (CORREGIDO)
   ========================================= */
.achievements-section { margin-bottom: 100px; }
.section-title { font-size: 2rem; font-weight: 700; color: white; margin-bottom: 5px; }

.achievements-grid {
  display: grid;
  /* Grid responsivo: mínimo 260px, se expande para llenar */
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  padding: 0 10px;
}

.achievement-box {
  background: rgba(255,255,255,0.03);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.05);
  position: relative;
  transition: transform 0.3s, border-color 0.3s;
  height: 100%;
}

.achievement-box:hover {
  transform: translateY(-8px);
  border-color: #00BCD4;
  background: rgba(255,255,255,0.05);
}

.achievement-year-badge {
  position: absolute; top: 0; right: 0;
  background: #00BCD4; color: #000;
  font-weight: bold; font-family: 'Courier New';
  padding: 5px 15px; border-bottom-left-radius: 16px;
}

.achievement-content-card { padding: 30px 25px; }

.icon-header { display: flex; justify-content: space-between; align-items: center; }
.icon-bg {
  width: 50px; height: 50px; background: rgba(0,188,212,0.1);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
}
.impact-tag {
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px;
  color: rgba(255,255,255,0.6); border: 1px solid rgba(255,255,255,0.2);
  padding: 4px 10px; border-radius: 20px;
}

/* =========================================
   CTA SECTION
   ========================================= */
.history-cta {
  padding: 60px 20px;
  background: linear-gradient(135deg, rgba(0,188,212,0.1), rgba(59,81,181,0.1));
  border-radius: 20px; border: 1px solid rgba(0,188,212,0.3);
  text-align: center;
}
.cta-title { font-size: 1.8rem; font-weight: bold; margin-bottom: 15px; }
.cta-text { font-size: 1.1rem; color: rgba(255,255,255,0.8); margin-bottom: 30px; }
.cta-button { background: linear-gradient(45deg, #00BCD4, #3D5AFE) !important; color: white !important; }
.cta-button-outline { border: 2px solid #00BCD4 !important; color: #00BCD4 !important; background: transparent !important; }

/* ANIMACIONES */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.scale-enter-active, .scale-leave-active { transition: all 0.3s; }
.scale-enter, .scale-leave-to { transform: translateX(-50%) scale(0.8); opacity: 0; }

/* MEDIA QUERIES ADICIONALES */
@media (max-width: 600px) {
  .line-1 { font-size: 1.8rem; }
  .line-2 { font-size: 3rem; }
  .timeline-3d-container { height: 450px; }
  .milestone-item-wrapper { width: 140px; }
  .section-title { font-size: 1.5rem; }
}
</style>